import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import InvitationLinkDialog from '../components/shared/InvitationLinkDialog';
import DroitsEquipe from '../components/DroitsEquipe';
import ComptesListe from '../components/equipe/ComptesListe';
import { ROLE_UTILISATEUR } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP04 — libellés du vocabulaire unique (lib/libelles.js, 09-VOCABULAIRE §4).
const roleLabels = {
  architect: ROLE_UTILISATEUR.architect,
  collaborateur: ROLE_UTILISATEUR.collaborateur,
  intervenant: 'Partenaire (BET, consultant)',
  amo: ROLE_UTILISATEUR.amo,
  admin: ROLE_UTILISATEUR.admin,
};

// RP32 / D1 — rôles proposés pour une personne du cabinet. Un partenaire (BET…) ou un
// AMO ne se crée plus ici : il s'invite depuis l'équipe du projet (RP11).
// RP33 / D3 — un associé ou un collaborateur voit tous les projets du cabinet, sauf
// « Accès restreint » (seulement les projets où on l'ajoute).
const AIDE_ROLE = {
  architect: "Voit tous les chantiers du cabinet ; valide, diffuse et engage le cabinet.",
  collaborateur: "Voit tous les chantiers du cabinet ; prépare (DPGF, CCTP, comptes rendus, visites, relances). L'architecte valide et diffuse.",
};

function AccesRestreintField({ value, onChange, testId }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={testId}>
      <input type="checkbox" className="mt-1" checked={!!value} onChange={e => onChange(e.target.checked)} />
      <span>
        <span className="font-medium text-[#2C2C2C]">Accès restreint</span>
        <span className="block text-xs text-[#6B6B6B]">
          Seulement les projets où cette personne est ajoutée à l'équipe (sinon : tous les projets du cabinet).
        </span>
      </span>
    </label>
  );
}

// RP43 / D8 — « Administrateur du cabinet » (comptes, charte, annuaire, e-mails) distinct
// d'« Associé » (voit tous les projets). Coché par défaut : comportement actuel.
function AssocieField({ value, onChange, testId }) {
  return (
    <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid={testId}>
      <input type="checkbox" className="mt-1" checked={value !== false} onChange={e => onChange(e.target.checked)} />
      <span>
        <span className="font-medium text-[#2C2C2C]">Associé : voit tous les projets</span>
        <span className="block text-xs text-[#6B6B6B]">
          Décochez pour un administrateur du cabinet qui gère les comptes, la charte, l'annuaire et les e-mails
          sans accès aux chantiers (sauf ceux où il est ajouté à l'équipe).
        </span>
      </span>
    </label>
  );
}

/**
 * RP84-85 — onglet « Cabinet » de l'écran unique « Équipe & accès » (ancienne page
 * « Équipe MOE », /equipe redirige ici) : inviter, modifier (rôle, accès restreint,
 * associé), liste des comptes avec leur statut (ComptesListe), droits de l'équipe.
 * RP86 — plus de suppression de compte ici : « Désactiver le compte » (liste) ; la
 * suppression définitive est dans la fiche › Avancé, seulement sans historique.
 */
export default function EquipeCabinet() {
  const { isAdmin, isArchitect } = useAuth();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  // RP06 — plus de « mot de passe initial » : la personne reçoit un lien pour choisir le sien
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'collaborateur', phone: '', acces_restreint: false });
  const [lienInvitation, setLienInvitation] = useState(null);
  const [rechargement, setRechargement] = useState(0);
  const fetchMembers = () => setRechargement((n) => n + 1);

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, newMember, { withCredentials: true });
      if (data?.email_sent) {
        toast.success(`Invitation envoyée à ${newMember.email}`);
      } else if (data?.activation_url) {
        toast.warning("Compte créé, mais l'e-mail n'est pas parti");
        setLienInvitation({ ...data, user_name: newMember.name, user_email: newMember.email });
      } else {
        toast.success('Compte créé');
      }
      setAddDialogOpen(false);
      setNewMember({ name: '', email: '', role: 'collaborateur', phone: '', acces_restreint: false });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/users/${editingMember._id}/admin`, {
        name: editingMember.name,
        email: editingMember.email,
        role: editingMember.role,
        phone: editingMember.phone || '',
        acces_restreint: !!editingMember.acces_restreint,
      }, { withCredentials: true });
      // RP43 / D8 — administrateur du cabinet associé ou non (réglage distinct)
      if (editingMember.role === 'admin' && (editingMember.is_associe !== false) !== (editingMember._associeInitial !== false)) {
        await axios.put(`${API_URL}/api/admin/users/${editingMember._id}/associe`,
          { is_associe: editingMember.is_associe !== false }, { withCredentials: true });
      }
      toast.success('Profil mis à jour');
      setEditDialogOpen(false);
      setEditingMember(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
      <div className="space-y-4" data-testid="equipe-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#6B6B6B]">
            Associés et collaborateurs du cabinet. Un AMO ou un partenaire s'invite depuis l'équipe du projet.
          </p>
          {(isAdmin || isArchitect) && (
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" data-testid="add-intervenant-button">
                <Plus size={18} weight="bold" />
                Inviter une personne
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Inviter une personne</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div>
                  <Label>Nom complet</Label>
                  <Input 
                    value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} 
                    placeholder="Marc Lejeune" required className="rounded-none"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} 
                    placeholder="marc@bet-structure.fr" required className="rounded-none"
                  />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={newMember.role} onValueChange={v => setNewMember({...newMember, role: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collaborateur">{roleLabels.collaborateur}</SelectItem>
                      <SelectItem value="architect">{roleLabels.architect}</SelectItem>
                    </SelectContent>
                  </Select>
                  {AIDE_ROLE[newMember.role] && (
                    <p className="text-xs text-[#6B6B6B] mt-1" data-testid="equipe-moe-role-aide">{AIDE_ROLE[newMember.role]}</p>
                  )}
                  {/* RP11 (D13 A) / RP32 — un AMO ou un partenaire (BET…) s'invite depuis l'équipe du projet */}
                  <p className="text-xs text-[#6B6B6B] mt-1" data-testid="equipe-moe-amo-note">
                    Un AMO ou un partenaire (BET, contrôleur technique…) s'invite depuis la fiche du projet (Équipe du projet › Inviter un AMO ou un partenaire).
                  </p>
                </div>
                <AccesRestreintField
                  value={newMember.acces_restreint}
                  onChange={v => setNewMember({ ...newMember, acces_restreint: v })}
                  testId="equipe-moe-acces-restreint"
                />
                <div>
                  <Label>Téléphone (optionnel)</Label>
                  <Input 
                    value={newMember.phone} onChange={e => setNewMember({...newMember, phone: e.target.value})} 
                    placeholder="06 12 34 56 78" className="rounded-none"
                  />
                </div>
                <p className="text-xs text-[#6B6B6B]">
                  La personne recevra un e-mail avec un lien pour choisir elle-même son mot de passe (valable 7 jours).
                </p>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-none">Annuler</Button>
                  <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" data-testid="invite-member-submit">Inviter</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        <ComptesListe
          groupe="cabinet"
          rechargement={rechargement}
          onModifier={isAdmin ? (m) => { setEditingMember({ ...m, _associeInitial: m.is_associe }); setEditDialogOpen(true); } : undefined}
        />

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Modifier le profil</DialogTitle>
            </DialogHeader>
            {editingMember && (
              <form onSubmit={handleEditMember} className="space-y-4">
                <div>
                  <Label>Nom complet</Label>
                  <Input value={editingMember.name} onChange={e => setEditingMember({...editingMember, name: e.target.value})} required className="rounded-none" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editingMember.email} onChange={e => setEditingMember({...editingMember, email: e.target.value})} required className="rounded-none" />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={editingMember.role} onValueChange={v => setEditingMember({...editingMember, role: v})}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{roleLabels.admin}</SelectItem>
                      <SelectItem value="architect">{roleLabels.architect}</SelectItem>
                      <SelectItem value="collaborateur">{roleLabels.collaborateur}</SelectItem>
                      {/* Comptes existants seulement : un AMO / partenaire s'invite depuis le projet */}
                      {editingMember.role === 'amo' && <SelectItem value="amo">{roleLabels.amo}</SelectItem>}
                      {editingMember.role === 'intervenant' && <SelectItem value="intervenant">{roleLabels.intervenant}</SelectItem>}
                    </SelectContent>
                  </Select>
                  {AIDE_ROLE[editingMember.role] && (
                    <p className="text-xs text-[#6B6B6B] mt-1">{AIDE_ROLE[editingMember.role]}</p>
                  )}
                </div>
                {editingMember.role === 'admin' && (
                  <AssocieField
                    value={editingMember.is_associe}
                    onChange={v => setEditingMember({ ...editingMember, is_associe: v })}
                    testId="equipe-moe-edit-associe"
                  />
                )}
                {['architect', 'collaborateur'].includes(editingMember.role) && (
                  <AccesRestreintField
                    value={editingMember.acces_restreint}
                    onChange={v => setEditingMember({ ...editingMember, acces_restreint: v })}
                    testId="equipe-moe-edit-acces-restreint"
                  />
                )}
                <div>
                  <Label>Téléphone</Label>
                  <Input value={editingMember.phone || ''} onChange={e => setEditingMember({...editingMember, phone: e.target.value})} className="rounded-none" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-none">Annuler</Button>
                  <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">Enregistrer</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <InvitationLinkDialog invitation={lienInvitation} onClose={() => setLienInvitation(null)} />

        {/* RP36-37 (D4) — droits réglés une fois par personne, pour tous les projets */}
        {isAdmin && <DroitsEquipe />}
      </div>
  );
}
